import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users, Plus, Pencil, Trash2, Loader2, Search, RefreshCw, 
  Image, Instagram, Eye, EyeOff, CheckCircle, XCircle, Upload, X
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Switch } from "../ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import LlmRegenerateButton from "./LlmRegenerateButton";
import SeoMetaEditor from "./SeoMetaEditor";
import ViewLiveLink from "./ViewLiveLink";

const POSITIONS = ["Goalkeeper", "Defender", "Midfielder", "Forward"];
const TEAMS = ["Men's First Team", "Women's First Team"];

const PlayersAdmin = () => {
  const { token } = useAuth();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scraping, setScraping] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState({ team: "all", squad: "all", position: "all" });
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sectionEnabled, setSectionEnabled] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [visibleCount, setVisibleCount] = useState(100);
  const fileInputRef = useRef(null);

  // Reset the visible window whenever the search or filters change
  useEffect(() => {
    setVisibleCount(100);
  }, [searchQuery, filter.team, filter.squad, filter.position]);

  const emptyPlayer = {
    name: "",
    years_played: "",
    positions: [],
    goals: 0,
    appearances: 0,
    biography: "",
    image_url: "",
    team: "Men's First Team",
    instagram: "",
    twitter: "",
    tiktok: "",
    is_current_squad: false
  };

  useEffect(() => {
    fetchPlayers();
    fetchSectionStatus();
  }, []);

  const fetchPlayers = async () => {
    try {
      // Backend defaults to limit=50; we want every player editable here.
      const response = await axios.get(`${API}/admin/players?limit=2000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlayers(response.data.players || response.data || []);
    } catch (error) {
      toast.error("Failed to fetch players");
    } finally {
      setLoading(false);
    }
  };

  const fetchSectionStatus = async () => {
    try {
      const response = await axios.get(`${API}/admin/section-toggles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Players doesn't have its own toggle but uses stats toggle
    } catch (error) {
      console.error("Failed to fetch section status");
    }
  };

  const scrapePlayerStats = async () => {
    setScraping(true);
    try {
      const response = await axios.post(`${API}/admin/players/scrape-stats`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message || "Player stats updated");
      fetchPlayers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to scrape stats");
    } finally {
      setScraping(false);
    }
  };

  const savePlayer = async () => {
    try {
      const payload = {
        ...editingPlayer,
        positions: Array.isArray(editingPlayer.positions) 
          ? editingPlayer.positions 
          : editingPlayer.positions?.split(',').map(p => p.trim()).filter(p => p) || []
      };
      
      if (editingPlayer.id) {
        await axios.put(`${API}/admin/players/${editingPlayer.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Player updated");
      } else {
        await axios.post(`${API}/admin/players`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Player created");
      }
      setDialogOpen(false);
      setEditingPlayer(null);
      fetchPlayers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save player");
    }
  };

  const deletePlayer = async (id) => {
    if (!confirm("Are you sure you want to delete this player?")) return;
    try {
      await axios.delete(`${API}/admin/players/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Player deleted");
      fetchPlayers();
    } catch (error) {
      toast.error("Failed to delete player");
    }
  };

  const filteredPlayers = players.filter(p => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!p.name?.toLowerCase().includes(query)) return false;
    }
    if (filter.team !== "all" && p.team !== filter.team) return false;
    if (filter.squad === "current" && !p.is_current_squad) return false;
    if (filter.squad === "former" && p.is_current_squad) return false;
    if (filter.position !== "all" && !p.positions?.includes(filter.position)) return false;
    return true;
  });

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const handleImageUpload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (JPG, PNG, GIF, WebP).");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB.");
      return;
    }
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const url = `/api/files/${res.data.storage_path}`;
      setEditingPlayer((cur) => ({ ...cur, image_url: url }));
      toast.success("Profile picture uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const clearImage = () => {
    setEditingPlayer((cur) => ({ ...cur, image_url: "" }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#7C2C3B]" /></div>;
  }

  return (
    <div data-testid="players-admin">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
          <Users className="w-8 h-8 inline mr-2 text-[#7C2C3B]" />
          PLAYERS ADMIN
        </h1>
        <div className="flex gap-2">
          <Button
            onClick={scrapePlayerStats}
            disabled={scraping}
            variant="outline"
            data-testid="scrape-stats-btn"
          >
            {scraping ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scraping...</>
            ) : (
              <><RefreshCw className="w-4 h-4 mr-2" /> Update Stats</>
            )}
          </Button>
          <Button
            onClick={() => { setEditingPlayer({...emptyPlayer}); setDialogOpen(true); }}
            className="bg-[#7C2C3B] hover:bg-[#63232F]"
            data-testid="add-player-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Player
          </Button>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-[#0F172A]">{players.length}</div>
          <div className="text-sm text-slate-500">Total Players</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-green-600">{players.filter(p => p.is_current_squad).length}</div>
          <div className="text-sm text-slate-500">Current Squad</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-[#7C2C3B]">{players.filter(p => p.team === "Men's First Team").length}</div>
          <div className="text-sm text-slate-500">Men's Team</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-purple-600">{players.filter(p => p.team === "Women's First Team").length}</div>
          <div className="text-sm text-slate-500">Women's Team</div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search players..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="player-search"
          />
        </div>
        <Select value={filter.team} onValueChange={(v) => setFilter({...filter, team: v})}>
          <SelectTrigger className="w-40"><SelectValue placeholder="Team" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Teams</SelectItem>
            <SelectItem value="Men's First Team">Men's</SelectItem>
            <SelectItem value="Women's First Team">Women's</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filter.squad} onValueChange={(v) => setFilter({...filter, squad: v})}>
          <SelectTrigger className="w-36"><SelectValue placeholder="Squad" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Players</SelectItem>
            <SelectItem value="current">Current Squad</SelectItem>
            <SelectItem value="former">Former Players</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filter.position} onValueChange={(v) => setFilter({...filter, position: v})}>
          <SelectTrigger className="w-36"><SelectValue placeholder="Position" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Positions</SelectItem>
            {POSITIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Players Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto max-h-[450px]">
          <Table>
            <TableHeader className="sticky top-0 bg-slate-100 z-10">
              <TableRow>
                <TableHead className="w-16">Photo</TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-24">Team</TableHead>
                <TableHead className="w-32">Position(s)</TableHead>
                <TableHead className="w-20">Goals</TableHead>
                <TableHead className="w-20">Apps</TableHead>
                <TableHead className="w-24">Current</TableHead>
                <TableHead className="w-28">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPlayers.slice(0, visibleCount).map(player => (
                <TableRow key={player.id}>
                  <TableCell>
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={player.image_url} alt={player.name} />
                      <AvatarFallback className="bg-[#7C2C3B] text-white text-xs">
                        {getInitials(player.name)}
                      </AvatarFallback>
                    </Avatar>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{player.name}</div>
                    <div className="text-xs text-slate-500">{player.years_played}</div>
                  </TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-1 rounded ${player.team === "Men's First Team" ? "bg-[#7C2C3B] text-white" : "bg-purple-600 text-white"}`}>
                      {player.team === "Men's First Team" ? "Men" : "Women"}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-slate-600">
                    {player.positions?.slice(0, 2).join(', ') || '-'}
                  </TableCell>
                  <TableCell className="font-bold">{player.goals || 0}</TableCell>
                  <TableCell className="font-bold">{player.appearances || 0}</TableCell>
                  <TableCell>
                    {player.is_current_squad ? (
                      <span className="flex items-center gap-1 text-green-600 text-xs">
                        <CheckCircle className="w-3 h-3" /> Yes
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-slate-400 text-xs">
                        <XCircle className="w-3 h-3" /> No
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <ViewLiveLink slug={player.slug} path="/players" testId={`player-${player.id}`} />
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 w-7 p-0"
                        onClick={() => { 
                          setEditingPlayer({
                            ...player,
                            positions: player.positions?.join(', ') || ''
                          }); 
                          setDialogOpen(true); 
                        }}
                        data-testid={`edit-player-${player.id}`}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                        onClick={() => deletePlayer(player.id)}
                        data-testid={`delete-player-${player.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      <div className="mt-3 flex flex-col items-center gap-2">
        <p className="text-xs text-slate-500">
          Showing {Math.min(visibleCount, filteredPlayers.length)} of {filteredPlayers.length} players
        </p>
        {filteredPlayers.length > visibleCount && (
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setVisibleCount((v) => v + 100)}
              data-testid="players-load-more"
            >
              Load 100 more
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setVisibleCount(filteredPlayers.length)}
              data-testid="players-load-all"
            >
              Show all ({filteredPlayers.length})
            </Button>
          </div>
        )}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingPlayer?.id ? "EDIT PLAYER" : "ADD NEW PLAYER"}
            </DialogTitle>
          </DialogHeader>
          
          {editingPlayer && (
            <div className="grid gap-4 py-4">
              <div className="flex justify-center">
                <Avatar className="h-24 w-24">
                  <AvatarImage src={editingPlayer.image_url} alt={editingPlayer.name} />
                  <AvatarFallback className="bg-[#7C2C3B] text-white text-2xl">
                    {getInitials(editingPlayer.name)}
                  </AvatarFallback>
                </Avatar>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input 
                    value={editingPlayer.name} 
                    onChange={(e) => setEditingPlayer({...editingPlayer, name: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Team</Label>
                  <Select value={editingPlayer.team || "Men's First Team"} onValueChange={(v) => setEditingPlayer({...editingPlayer, team: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TEAMS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Years Played</Label>
                  <Input 
                    value={editingPlayer.years_played || ""} 
                    onChange={(e) => setEditingPlayer({...editingPlayer, years_played: e.target.value})}
                    placeholder="e.g. 2020-2024"
                  />
                </div>
                <div>
                  <Label>Positions (comma-separated, max 4)</Label>
                  <Input 
                    value={editingPlayer.positions || ""} 
                    onChange={(e) => setEditingPlayer({...editingPlayer, positions: e.target.value})}
                    placeholder="e.g. Forward, Midfielder"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Goals</Label>
                  <Input 
                    type="number"
                    min="0"
                    value={editingPlayer.goals || 0} 
                    onChange={(e) => setEditingPlayer({...editingPlayer, goals: parseInt(e.target.value) || 0})}
                  />
                </div>
                <div>
                  <Label>Appearances</Label>
                  <Input 
                    type="number"
                    min="0"
                    value={editingPlayer.appearances || 0} 
                    onChange={(e) => setEditingPlayer({...editingPlayer, appearances: parseInt(e.target.value) || 0})}
                  />
                </div>
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Biography (100 words · LLM-editable)</Label>
                  {editingPlayer.id && (
                    <LlmRegenerateButton
                      collection="players"
                      docId={editingPlayer.id}
                      onText={(text) => setEditingPlayer({ ...editingPlayer, biography: text })}
                      testId="players-regenerate-biography"
                    />
                  )}
                </div>
                <Textarea 
                  value={editingPlayer.biography || ""} 
                  onChange={(e) => setEditingPlayer({...editingPlayer, biography: e.target.value})}
                  rows={5}
                  data-testid="players-biography-textarea"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {editingPlayer.biography?.split(/\s+/).filter(w => w).length || 0} words · Your edits are never overwritten by the LLM.
                </p>
              </div>
              
              <div>
                <Label>Profile Picture</Label>
                <div className="space-y-2 mt-1">
                  {/* Upload from device */}
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImage}
                      data-testid="player-image-upload-btn"
                    >
                      {uploadingImage ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading…</>
                      ) : (
                        <><Upload className="w-4 h-4 mr-2" /> Upload from device</>
                      )}
                    </Button>
                    {editingPlayer.image_url && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={clearImage}
                        data-testid="player-image-clear-btn"
                      >
                        <X className="w-4 h-4 mr-1" /> Clear
                      </Button>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e.target.files?.[0])}
                      data-testid="player-image-file-input"
                    />
                    <span className="text-xs text-slate-500">JPG / PNG / GIF / WebP · max 5MB</span>
                  </div>
                  {/* Or paste an external URL */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 whitespace-nowrap">or paste URL:</span>
                    <Input
                      value={editingPlayer.image_url || ""}
                      onChange={(e) => setEditingPlayer({ ...editingPlayer, image_url: e.target.value })}
                      placeholder="https://example.com/photo.jpg"
                      data-testid="player-image-url-input"
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="flex items-center gap-1">
                    <Instagram className="w-4 h-4" /> Instagram Handle
                  </Label>
                  <Input 
                    value={editingPlayer.instagram || ""} 
                    onChange={(e) => setEditingPlayer({...editingPlayer, instagram: e.target.value})}
                    placeholder="without @"
                    data-testid="edit-player-instagram"
                  />
                </div>
                <div>
                  <Label>X / Twitter Handle</Label>
                  <Input
                    value={editingPlayer.twitter || ""}
                    onChange={(e) => setEditingPlayer({...editingPlayer, twitter: e.target.value})}
                    placeholder="without @"
                    data-testid="edit-player-twitter"
                  />
                </div>
                <div>
                  <Label>TikTok Handle</Label>
                  <Input 
                    value={editingPlayer.tiktok || ""} 
                    onChange={(e) => setEditingPlayer({...editingPlayer, tiktok: e.target.value})}
                    placeholder="without @"
                    data-testid="edit-player-tiktok"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-3 pt-2">
                <Switch
                  checked={editingPlayer.is_current_squad}
                  onCheckedChange={(v) => setEditingPlayer({...editingPlayer, is_current_squad: v})}
                />
                <Label className="mb-0">Current 2025-26 Squad Member</Label>
              </div>

              {/* SEO metadata editor — collapsible. Backend auto-clears
                  seo_* when name/biography change, so this panel will
                  show a "stale" banner until the admin regenerates. */}
              {editingPlayer.id && (
                <SeoMetaEditor collection="players" docId={editingPlayer.id} />
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={savePlayer} className="bg-[#7C2C3B] hover:bg-[#63232F]" data-testid="save-player-btn">
              {editingPlayer?.id ? "Update" : "Create"} Player
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlayersAdmin;
